import { OBSERVATION_POLICY } from "./observation";

export interface Resource<T> { data: T | null; error: string | null }
export const emptyResource = <T>(): Resource<T> => ({ data: null, error: null });

/** Follow only a nonterminal success; each attempt remains a bounded one-shot. */
export function followResource<T>(
  read: (signal: AbortSignal) => Promise<T>,
  publish: (state: Resource<T>, received: boolean) => void,
  again: (data: T) => boolean,
  policy: Pick<typeof OBSERVATION_POLICY, "pollMs" | "timeoutMs"> = OBSERVATION_POLICY,
): () => void {
  const run = {
    active: true, pending: true, last: null as T | null,
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
    cancel: () => {},
  };
  const start = () => {
    clearTimeout(run.timer);
    if (!run.active || !run.pending || document.hidden) return;
    run.pending = false;
    run.cancel = loadResource(read, state => {
      if (!run.active) return;
      // Automatic follow keeps the last record; errors annotate that observation.
      if (state.data !== null) run.last = state.data;
      publish(state.data === null && run.last !== null ? { data: run.last, error: state.error } : state, state.data !== null);
      if (state.data === null || !again(state.data)) return;
      run.pending = true;
      if (!document.hidden) run.timer = setTimeout(start, policy.pollMs);
    }, policy.timeoutMs);
  };
  const visibility = () => {
    if (document.hidden) clearTimeout(run.timer);
    if (!document.hidden) start();
  };
  document.addEventListener("visibilitychange", visibility);
  start();
  return () => {
    run.active = false;
    clearTimeout(run.timer);
    document.removeEventListener("visibilitychange", visibility);
    run.cancel();
  };
}

/** One bounded read, never a poll. Cleanup also invalidates abort-ignoring work. */
export function loadResource<T>(
  read: (signal: AbortSignal) => Promise<T>,
  publish: (state: Resource<T>) => void,
  timeoutMs = OBSERVATION_POLICY.timeoutMs,
): () => void {
  const run = { active: true };
  const controller = new AbortController();
  const finish = (state: Resource<T>) => {
    if (!run.active) return;
    run.active = false;
    clearTimeout(deadline);
    controller.abort();
    publish(state);
  };
  const deadline = setTimeout(() => finish({ data: null, error: "Record request timed out. Try again." }), timeoutMs);
  publish(emptyResource());
  Promise.resolve().then(() => run.active ? read(controller.signal) : null).then(
    (data) => finish(data === null ? { data: null, error: "Record unavailable" } : { data, error: null }),
    (error: unknown) => finish({ data: null, error: error instanceof Error ? error.message : "Record request failed" }),
  );
  return () => {
    run.active = false;
    clearTimeout(deadline);
    controller.abort();
  };
}
